import React, { useRef } from 'react';
import { useNearViewport } from '../hooks/useNearViewport';
import { motion, useAnimationFrame, useMotionValue, useReducedMotion, useTransform, wrap, type PanInfo } from 'framer-motion';
import type { ProjectImage } from '../data/carousels';
type ProjectCarouselProps = {
  images: ProjectImage[];
  direction?: 'left' | 'right';
  /** Seconds for one full pass of the image sequence. */
  durationSeconds?: number;
};
export function ProjectCarousel({
  images,
  direction = 'left',
  durationSeconds = 35
}: ProjectCarouselProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const prefersReducedMotion = useReducedMotion();
  // Images only mount once the strip approaches the viewport, so the ~62 tiles
  // never compete with the hero or the pre-warmed upsell image on first load.
  const {
    ref: containerRef,
    isNear
  } = useNearViewport<HTMLDivElement>();
  const basePercent = useMotionValue(0);
  const x = useTransform(basePercent, value => `${wrap(-50, 0, value)}%`);
  useAnimationFrame((_time, delta) => {
    if (prefersReducedMotion || isDraggingRef.current) return;
    const percentPerMs = 50 / (durationSeconds * 1000);
    const step = percentPerMs * delta * (direction === 'left' ? -1 : 1);
    basePercent.set(basePercent.get() + step);
  });
  const handlePanStart = () => {
    isDraggingRef.current = true;
  };
  const handlePan = (_event: PointerEvent, info: PanInfo) => {
    const trackWidth = trackRef.current?.offsetWidth ?? 1;
    basePercent.set(basePercent.get() + info.delta.x / trackWidth * 100);
  };
  const handlePanEnd = () => {
    isDraggingRef.current = false;
  };
  const loop = [...images, ...images];
  const cardWidth = 'clamp(148px, 44vw, 188px)';
  if (!isNear) {
    // Same footprint as the loaded strip, so nothing shifts when it mounts.
    return <div ref={containerRef} className="relative w-full overflow-hidden" style={{
      height: cardWidth
    }} aria-hidden="true" data-id="element-107" data-mp="2tky66dxz2d62" />;
  }
  return <div ref={containerRef} className="relative w-full overflow-hidden" data-id="element-108" data-mp="2vmyq0o8sibwz">
      <motion.div ref={trackRef} style={{
      x,
      touchAction: 'pan-y'
    }} onPanStart={handlePanStart} onPan={handlePan} onPanEnd={handlePanEnd} className="flex w-max cursor-grab active:cursor-grabbing" data-id="element-109" data-mp="30l0b1eps0lb3">
        {loop.map((image, index) => <div key={`${image.src}-${index}`} className="mr-2.5 shrink-0" style={{
        width: cardWidth
      }} data-id="element-110" data-mp="2cxs9n7vgxwec" data-mpi={index}>
            <img src={image.src} alt={index < images.length ? image.alt : ''} aria-hidden={index >= images.length} draggable={false} width={512} height={512} loading="lazy" decoding="async" className="aspect-square w-full select-none rounded-2xl object-cover" data-id="element-111" data-mp="2muwvf6lejpum" />
          </div>)}
      </motion.div>
    </div>;
}